import { type LinkedValueStatus, type RtmSetFieldWrite } from '@onestep/camel-api';
import { useQuery } from '@tanstack/react-query';

import useStores from '../../hooks/useStores';
import { logCustomEventsError, patientCustomEventsQueryKey, seriesCatalogQueryKey } from './consts';
import { useCustomEventMutations } from './useCustomEventMutations';

export type CustomEventFieldType =
  | 'text'
  | 'int'
  | 'float'
  | 'date'
  | 'datetime'
  | 'select'
  | 'radio'
  | 'checkbox';

export type CustomEventFieldOption = {
  label: string;
  value: string;
};

export type CustomEventDefinitionField = {
  name: string;
  label: string;
  type: CustomEventFieldType;
  required: boolean;
  helpText?: string;
  options?: CustomEventFieldOption[];
};

export type CustomEventSeriesStatus = 'active' | 'archived';

export type CustomEventEffectiveTimeGranularity = 'date' | 'datetime';

export type CustomEventSeriesDefinition = {
  slug: string;
  name: string;
  version: number;
  status: CustomEventSeriesStatus;
  effectiveTimeGranularity: CustomEventEffectiveTimeGranularity;
  fields: CustomEventDefinitionField[];
};

export type CustomEventValues = Record<string, unknown>;

export type CustomEventEntry = {
  id: string;
  seriesSlug: string;
  version: number;
  effectiveTime: string;
  isDateOnly: boolean;
  values: CustomEventValues;
  createdAt: string;
  updatedAt?: string;
};

export type CustomEventSeriesEntries = {
  slug: string;
  name: string;
  status: CustomEventSeriesStatus;
  current: CustomEventEntry | null;
  entries: CustomEventEntry[];
};

export type CustomEventDefinitionsMap = Record<string, CustomEventSeriesDefinition>;

export type SeriesCatalogResponse = {
  series: CustomEventSeriesDefinition[];
};

export type PatientCustomEventsResponse = {
  series: CustomEventSeriesEntries[];
  definitions: CustomEventDefinitionsMap;
};

export type CustomEventPayload = {
  effectiveTime: string;
  values: CustomEventValues;
};

export type CreateEntryVariables = {
  seriesSlug: string;
  payload: CustomEventPayload;
};

export type AmendEntryVariables = {
  seriesSlug: string;
  entryId: string;
  payload: CustomEventPayload;
};

export type DeleteEntryVariables = {
  seriesSlug: string;
  entryId: string;
};

export type SetFieldVariables = {
  rtm?: RtmSetFieldWrite;
  linkedAttributeId?: string;
  seriesSlug: string;
  field: string;
  // Scalar for most field types; a list of option values for checkbox fields;
  // numbers for int/float fields.
  value: string | number | boolean | string[];
};

export type SetFieldResult = {
  action: 'created' | 'amended' | 'noop';
  entry?: CustomEventEntry | null;
  displayValue?: string | null;
  displayStatus?: LinkedValueStatus;
  displaySource?: string | null;
  displaySourceAt?: string | null;
};

export type SetFieldOutcome = SetFieldResult & { isRefreshFailed?: boolean };

export const definitionKey = (slug: string, version: number) => `${slug}:${version}`;

export const resolveEntryDefinition = (
  entry: Pick<CustomEventEntry, 'seriesSlug' | 'version'>,
  definitions: CustomEventDefinitionsMap | undefined,
): CustomEventSeriesDefinition | undefined =>
  definitions?.[definitionKey(entry.seriesSlug, entry.version)];

export const useCustomEventsSeriesCatalog = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { appStore } = useStores();
  const workspaceUuid = appStore.clinicUuid;

  const { data, isLoading, isError } = useQuery<SeriesCatalogResponse>({
    queryKey: seriesCatalogQueryKey(workspaceUuid),
    enabled: enabled && !!workspaceUuid,
    onError: (error) => logCustomEventsError('custom_events_series_catalog_error', {}, error),
  });

  return {
    seriesCatalog: data?.series,
    isSeriesCatalogLoading: isLoading,
    isSeriesCatalogError: isError,
  };
};

export const useCustomEventsService = ({ patientUuid }: { patientUuid: string }) => {
  const { appStore } = useStores();
  const workspaceUuid = appStore.clinicUuid;

  const { seriesCatalog, isSeriesCatalogLoading, isSeriesCatalogError } =
    useCustomEventsSeriesCatalog();

  const {
    data: patientEvents,
    isLoading: isPatientEventsLoading,
    isError: isPatientEventsError,
  } = useQuery<PatientCustomEventsResponse>({
    queryKey: patientCustomEventsQueryKey(workspaceUuid, patientUuid),
    enabled: !!workspaceUuid && !!patientUuid,
    onError: (error) => logCustomEventsError('custom_events_patient_entries_error', {}, error),
  });

  const { createEntry, amendEntry, deleteEntry } = useCustomEventMutations({ patientUuid });

  return {
    seriesCatalog,
    isSeriesCatalogLoading,
    isSeriesCatalogError,
    patientSeries: patientEvents?.series,
    definitions: patientEvents?.definitions,
    isPatientEventsLoading,
    isPatientEventsError,
    createEntry,
    amendEntry,
    deleteEntry,
  };
};
